import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BigFiveModal from '../components/BigFiveModal';
import { Camera } from 'lucide-react';

const BRANCH_OPTIONS = [
  'Erneuerbare Energien (Solar, Wind, Wasserkraft)',
  'Energiespeicher / Batterietechnologie',
  'Wasserstoff / Power-to-X',
  'E-Mobilität / Nachhaltige Mobilität',
  'Umwelttechnik / Kreislaufwirtschaft',
  'Wasser- / Abwassermanagement',
  'Nachhaltige Land- und Forstwirtschaft',
  'Klimaforschung / Geowissenschaften',
  'Green Building / Nachhaltiges Bauen',
  'ESG / Nachhaltigkeitsberatung',
  'Biodiversität / Naturschutz',
  'Fernerkundung / Umweltmonitoring',
  'Chemie / Verfahrenstechnik (grün)',
  'IT / Software (GreenTech-Bezug)',
  'Forschung / Wissenschaft',
  'Sonstiges',
];

function MentorProfilePage() {
  const [firstTimeSaved, setFirstTimeSaved] = useState(false);
  const [showBigFiveModal, setShowBigFiveModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [bigfiveData, setBigfiveData] = useState({});

  // NEU: merken, ob bereits eine Big-Five-Analyse vorliegt
  const [hasBigFive, setHasBigFive] = useState(false);

  const [avatarPath, setAvatarPath] = useState('');
  const [remoteImageBase64, setRemoteImageBase64] = useState(null);

  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [mailValue, setMailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [standortValue, setStandortValue] = useState('');
  const [abteilungValue, setAbteilungValue] = useState('');
  const [jobTitleValue, setJobTitleValue] = useState('');
  const [branchenKnowValue, setBranchenKnowValue] = useState([]);
  const [experienceValue, setExperienceValue] = useState('');
  const [goals, setGoals] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [whyMentoring, setWhyMentoring] = useState('');

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const isDE = String(i18n?.language || '').toLowerCase().startsWith('de');
  const tr = (de, en) => (isDE ? de : en);
  const Req = () => <span className="text-red-400 ml-1">*</span>;

  // ALLES aus mentorProfile ziehen!
  const devGoals = t('mentorProfile.devGoals', { returnObjects: true }) || [];
  const competenceData = t('mentorProfile.competenceData', { returnObjects: true }) || [];
  const abteilungOptions = t('mentorProfile.departmentOptions', { returnObjects: true }) || [];
  const experienceOptions = t('mentorProfile.experienceOptions', { returnObjects: true }) || [];
  const bigFiveTraits = t('mentorProfile.bigFiveTraits', { returnObjects: true }) || [];

  // ✅ NEU: Onboarding-Milestone setzen, sobald Profilseite erreicht ist
  useEffect(() => {
    axios
      .post('/mentoring/onboarding/complete', null, { withCredentials: true })
      .catch(() => {
        // best effort – absichtlich still
      });
  }, []);

  useEffect(() => {
    axios.get('/mentoring/mentor/profile', { withCredentials: true })
      .then((res) => {
        const data = res.data;
        if (data.avatarPath) setAvatarPath(data.avatarPath.replace(/\\/g, '/'));
        if (data.avatar) setRemoteImageBase64(`data:image/png;base64,${data.avatar}`);
        if (data.email) setMailValue(data.email);
        if (data.firstName) setFirstNameValue(data.firstName);
        if (data.lastName) setLastNameValue(data.lastName);
        if (data.phone) setPhoneValue(data.phone);
        if (data.standort) setStandortValue(data.standort);
        if (data.abteilung) setAbteilungValue(data.abteilung);
        if (data.jobTitle) setJobTitleValue(data.jobTitle);
        try {
          const parsed = JSON.parse(data.profileDescription);
          setBranchenKnowValue(Array.isArray(parsed) ? parsed : []);
        } catch {
          setBranchenKnowValue([]);
        }
        if (data.experience) setExperienceValue(data.experience);
        if (Array.isArray(data.goals)) setGoals(data.goals);
        if (Array.isArray(data.competences)) setCompetences(data.competences);
        if (data.whyMentoring) setWhyMentoring(data.whyMentoring);
        if (data.user_id) setUserId(data.user_id);

        // Big-Five-Daten übernehmen …
        if (data.bigfive_extraversion) {
          setBigfiveData({
            extraversion: data.bigfive_extraversion,
            gewissenhaftigkeit: data.bigfive_gewissenhaftigkeit,
            negative_emotionalitaet: data.bigfive_negative_emotionalitaet,
            vertraeglichkeit: data.bigfive_vertraeglichkeit,
            offenheit: data.bigfive_offenheit,
            extraversion_text: data.bigfive_extraversion_text,
            gewissenhaftigkeit_text: data.bigfive_gewissenhaftigkeit_text,
            negative_emotionalitaet_text: data.bigfive_negative_emotionalitaet_text,
            vertraeglichkeit_text: data.bigfive_vertraeglichkeit_text,
            offenheit_text: data.bigfive_offenheit_text
          });
        }

        // … und erkennen, ob eine Analyse bereits existiert
        // (reichen lässt: Zeitstempel oder irgendein Score vorhanden)
        const alreadyHas = Boolean(data.bigfive_updated_at)
                        || Boolean(data.bigfive_extraversion);
        setHasBigFive(alreadyHas);
      })
      .catch(console.error);
  }, []);

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    axios.post('/mentoring/upload/image', formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(res => {
        setAvatarPath(res.data.filePath.replace(/^uploads[\\/]+/, ''));
      })
      .catch(console.error);
  }

  // MAX 4 Entwicklungsziele auswählbar
  function handleDevGoalChange(goal) {
    if (goals.includes(goal)) {
      setGoals(goals.filter(g => g !== goal));
    } else if (goals.length < 4) {
      setGoals([...goals, goal]);
    }
  }

  // MAX 16 Kompetenzen auswählbar
  function handleCompetenceChange(item) {
    if (competences.includes(item)) {
      setCompetences(competences.filter(c => c !== item));
    } else if (competences.length < 16) {
      setCompetences([...competences, item]);
    }
  }

  // ✅ Pflichtfelder + Gate für "Profil speichern"
  const firstNameOk   = String(firstNameValue  || '').trim().length > 0;
  const lastNameOk    = String(lastNameValue   || '').trim().length > 0;
  const departmentOk  = String(abteilungValue  || '').trim().length > 0;
  const jobTitleOk    = String(jobTitleValue   || '').trim().length > 0;
  const branchenOk    = Array.isArray(branchenKnowValue) && branchenKnowValue.length > 0;
  const experienceOk  = String(experienceValue || '').trim().length > 0;
  const goalsOk       = Array.isArray(goals)       && goals.length > 0;
  const competencesOk = Array.isArray(competences)  && competences.length > 0;

  const profileIsValid =
    firstNameOk && lastNameOk && departmentOk &&
    jobTitleOk && branchenOk && experienceOk && goalsOk && competencesOk;

  const missingProfileFields = [];
  if (!firstNameOk)   missingProfileFields.push(tr('Vorname', 'First name'));
  if (!lastNameOk)    missingProfileFields.push(tr('Nachname', 'Last name'));
  if (!departmentOk)  missingProfileFields.push(tr('Abteilung', 'Department'));
  if (!jobTitleOk)    missingProfileFields.push(tr('Position', 'Job title'));
  if (!branchenOk)    missingProfileFields.push(tr('Branchen-Know-how', 'Industry knowledge'));
  if (!experienceOk)  missingProfileFields.push(tr('Berufserfahrung', 'Experience'));
  if (!goalsOk)       missingProfileFields.push(tr('Entwicklungsziele', 'Development goals'));
  if (!competencesOk) missingProfileFields.push(tr('Kompetenzen', 'Competences'));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!profileIsValid) {
      alert(tr('Bitte alle Pflichtfelder ausfüllen.', 'Please fill all required fields.'));
      return;
    }
    try {
      const formData = new FormData();
      formData.append('avatarPath', avatarPath);
      formData.append('firstName', firstNameValue);
      formData.append('lastName', lastNameValue);
      formData.append('email', mailValue);
      formData.append('phone', phoneValue);
      formData.append('standort', standortValue);
      formData.append('abteilung', abteilungValue);
      formData.append('jobTitle', jobTitleValue);
      formData.append('branchenKnow', JSON.stringify(branchenKnowValue));
      formData.append('experience', experienceValue);
      formData.append('goals', JSON.stringify(goals));
      formData.append('competences', JSON.stringify(competences));
      formData.append('whyMentoring', whyMentoring);

      await axios.post('/mentoring/mentor/profile', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(t('mentorProfile.saveSuccess'));

      // NEU: Modal NUR zeigen, wenn (1) erstes Speichern in dieser Sitzung
      // und (2) noch KEINE Big-Five-Analyse existiert.
      if (!firstTimeSaved && !hasBigFive) {
        setFirstTimeSaved(true);
        setShowBigFiveModal(true);
      }
    } catch (err) {
      console.error(err);
      alert(t('mentorProfile.saveError'));
    }
  }

  // wie beim Mentee: über Nginx-Proxy ausliefern
  const displayedImage = avatarPath
    ? `/mentoring/uploads/${avatarPath}`
    : remoteImageBase64 || null;

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-start mb-6">
        <button
          type="button"
          onClick={() => navigate('/mentor-dashboard')}
          className="flex items-center gap-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          <span className="text-xl"></span> {t('mentorProfile.backToDashboard')}
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">{t('mentorProfile.heading')}</h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profilbild-Kachel */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow flex flex-col items-center">
          <div className="relative w-32 h-32">
            {displayedImage ? (
              <img src={displayedImage} alt={t('mentorProfile.avatarAlt')} className="w-32 h-32 rounded-full object-cover shadow-md" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">{t('mentorProfile.noAvatar')}</div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-60 rounded-full cursor-pointer">
              <Camera className="w-8 h-8 text-white opacity-0 hover:opacity-100" />
              <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
            </label>
          </div>
          <p className="text-sm text-gray-400 mt-2">{t('mentorProfile.changeAvatar')}</p>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>{t('mentorProfile.firstName')}<Req /></label>
          <input type="text" value={firstNameValue} onChange={(e) => setFirstNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.lastName')}<Req /></label>
          <input type="text" value={lastNameValue} onChange={(e) => setLastNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.email')}</label>
          <input type="email" value={mailValue} readOnly className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>{t('mentorProfile.phone')}</label>
          <input type="text" value={phoneValue} onChange={(e) => setPhoneValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.location')}</label>
          <input type="text" value={standortValue} onChange={(e) => setStandortValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.department')}<Req /></label>
          <select value={abteilungValue} onChange={(e) => setAbteilungValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {Array.isArray(abteilungOptions) && abteilungOptions.map((opt, i) => (
              <option key={opt || i} value={opt}>{opt}</option>
            ))}
          </select>
          <label>{t('mentorProfile.jobTitle')}<Req /></label>
          <input type="text" value={jobTitleValue} onChange={(e) => setJobTitleValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <p className="text-lg font-semibold mb-2">
            {t('mentorProfile.branchKnow')}
            <Req />
          </p>
          <div className="space-y-1 mb-4">
            {BRANCH_OPTIONS.map((branch) => (
              <div key={branch} className="flex items-center">
                <input
                  type="checkbox"
                  checked={branchenKnowValue.includes(branch)}
                  onChange={() => {
                    setBranchenKnowValue((prev) =>
                      prev.includes(branch)
                        ? prev.filter((b) => b !== branch)
                        : [...prev, branch]
                    );
                  }}
                  className="mr-2"
                />
                <label>{branch}</label>
              </div>
            ))}
          </div>
          <label>{t('mentorProfile.experience')}<Req /></label>
          <select value={experienceValue} onChange={(e) => setExperienceValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {Array.isArray(experienceOptions) && experienceOptions.map((opt, i) => (
              <option key={opt || i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-6 bg-gray-800 p-6 rounded-2xl shadow md:col-span-2">
          <div>
            <p className="text-lg font-semibold mb-2">{t('mentorProfile.goalsLabel')} <span className="text-xs text-gray-400">({t('mentorProfile.goalsMax')})</span><Req /></p>
            {Array.isArray(devGoals) && devGoals.map((goal) => (
              <div key={goal} className="flex items-center mb-1">
                <input
                  type="checkbox"
                  checked={goals.includes(goal)}
                  onChange={() => handleDevGoalChange(goal)}
                  disabled={!goals.includes(goal) && goals.length >= 4}
                  className="mr-2"
                />
                <label>{goal}</label>
              </div>
            ))}
            {goals.length >= 4 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('mentorProfile.goalsLimit')}</div>
            )}
          </div>

          <div>
            <p className="text-lg font-semibold mb-2">{t('mentorProfile.competencesLabel')} <span className="text-xs text-gray-400">({t('mentorProfile.competencesMax')})</span><Req /></p>
            {Array.isArray(competenceData) && competenceData.map((cat) => (
              <div key={cat.kategorie} className="mb-6">
                <strong className="block mb-2">{cat.kategorie}</strong>
                <div className="grid grid-cols-2 gap-2 ml-4">
                  {cat.items.map((item) => (
                    <div key={item} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={competences.includes(item)}
                        onChange={() => handleCompetenceChange(item)}
                        disabled={!competences.includes(item) && competences.length >= 16}
                        className="mr-2"
                      />
                      <label>{item}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {competences.length >= 16 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('mentorProfile.competencesLimit')}</div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow md:col-span-2">
          <label>{t('mentorProfile.whyMentoring')}</label>
          <textarea value={whyMentoring} onChange={(e) => setWhyMentoring(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        <button
          type="submit"
          disabled={!profileIsValid}
          className={`bg-mentixxgreen w-full py-2 rounded text-black font-bold md:col-span-2 ${!profileIsValid ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {t('mentorProfile.saveProfile')}
        </button>

        {!profileIsValid && (
          <div className="md:col-span-2 text-xs text-yellow-200">
            {tr('Bitte noch ausfüllen:', 'Please fill:')} {missingProfileFields.join(', ')}
          </div>
        )}
      </form>

      {/* Big Five Analyse */}
      {bigfiveData.extraversion && (
        <div className="mt-10 flex justify-center">
          <div className="bg-[#23272a] rounded-3xl shadow-lg p-8 max-w-xl w-full mx-auto">
            <div className="text-2xl font-bold text-center mb-6 text-white tracking-tight">{t('mentorProfile.bigFiveTitle')}</div>
            <div className="space-y-7 mb-5">
              {Array.isArray(bigFiveTraits) && bigFiveTraits.map((trait) => {
                const raw = parseFloat(bigfiveData[trait.key]) || 0;
                const percent = Math.round((raw / 5) * 100);
                return (
                  <div key={trait.key}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold" style={{ color: trait.color }}>{trait.label}</span>
                      <span className="text-xs text-gray-300">{percent} / 100</span>
                    </div>
                    <div className="relative flex items-center h-6 mb-1">
                      <span className="absolute left-0 text-xs text-gray-400 select-none" title={trait.min}>0</span>
                      <span className="absolute right-0 text-xs text-gray-400 select-none" title={trait.max}>100</span>
                      <div className="absolute left-0 w-full h-3 bg-gray-900 rounded-full" />
                      <div
                        className="absolute left-0 h-3 rounded-full"
                        style={{
                          width: `${percent}%`,
                          background: `linear-gradient(90deg, ${trait.color} 70%, #23272a 100%)`
                        }}
                        title={percent < 34 ? trait.min : percent < 67 ? trait.mid : trait.max}
                      />
                      <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-6 bg-gray-600 rounded opacity-60" title={trait.mid}></div>
                    </div>
                    <div className="text-xs text-gray-400 text-center mt-0.5">{`50 / 100: ${trait.mid}`}</div>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#1a1b1f] rounded-xl p-5 shadow-inner mt-7 space-y-3 text-[15px] leading-relaxed">
              <div><b>{t('mentorProfile.traitExtraversion')}:</b> {bigfiveData.extraversion_text || '—'}</div>
              <div><b>{t('mentorProfile.traitConscientiousness')}:</b> {bigfiveData.gewissenhaftigkeit_text || '—'}</div>
              <div><b>{t('mentorProfile.traitEmotionalStability')}:</b> {bigfiveData.negative_emotionalitaet_text || '—'}</div>
              <div><b>{t('mentorProfile.traitAgreeableness')}:</b> {bigfiveData.vertraeglichkeit_text || '—'}</div>
              <div><b>{t('mentorProfile.traitOpenness')}:</b> {bigfiveData.offenheit_text || '—'}</div>
            </div>
          </div>
        </div>
      )}

      <BigFiveModal
        visible={showBigFiveModal}
        onClose={() => setShowBigFiveModal(false)}
        onStartTest={async () => {
          setShowBigFiveModal(false);
          const newWindow = window.open('about:blank', '_blank');

          // Sprache aus URL oder i18n bestimmen (2-stellig)
          const urlLang =
            new URLSearchParams(window.location.search).get('lang') ||
            (typeof i18n?.language === 'string' ? i18n.language : 'de');
          const lang = String(urlLang).slice(0, 2);

          // ✅ NEU: Auth-User-ID holen (nicht mentoring_user_id)
          const who = await fetch('/whoami', { credentials: 'include' })
            .then(r => r.json())
            .catch(() => null);

          const authUserId = who?.user?.id;
          if (!authUserId) {
            if (newWindow) newWindow.close();
            alert('Login fehlt oder Session abgelaufen. Bitte neu einloggen.');
            return;
          }

          // BigFive läuft unter /bigfive/app/ (wichtig: trailing slash!)
          const url = `/bigfive/app/?user_id=${authUserId}&lng=${lang}`;
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      />
    </div>
  );
}

export default MentorProfilePage;
